import {
  ExecutionCandlesBackfillAccepted,
  fetchExecutionCandles,
  fetchOhlcStrip,
  type ExecutionCandlesFetchParams,
} from "../services/tradeStatisticsApi";
import type {
  ExecutionCandlesBackfillProgress,
  ExecutionCandlesResponse,
  OhlcStripPollResponse,
} from "../types/trade";
import { executionCandlesParamsCachePayload } from "./executionCandlesParams";
import { EXECUTION_CANDLES_SOURCE_UNAVAILABLE_MESSAGE } from "./executionCandlesStatus";
import {
  MarketDataUnavailableError,
  marketDataUnavailableFromBrokerAuthFailure,
} from "./marketDataUnavailable";

/** Share one backfill poll + retry across concurrent identical candle loads. */
const executionCandlesLoadsInFlight = new Map<string, Promise<ExecutionCandlesResponse>>();
const ohlcStripPollsInFlight = new Map<string, Promise<OhlcStripPollResponse>>();

/** Drop in-flight load dedupe for superseded chart context. */
export function abortExecutionCandlesLoad(params: ExecutionCandlesFetchParams): void {
  const key = executionCandlesLoadKey({
    ...params,
    fill_missing: params.fill_missing ?? true,
  });
  executionCandlesLoadsInFlight.delete(key);
}

/** Drop in-flight strip poll dedupe for superseded chart context. */
export function abortOhlcStripPoll(pollUrl: string): void {
  ohlcStripPollsInFlight.delete(pollUrl);
}

function executionCandlesLoadKey(params: ExecutionCandlesFetchParams): string {
  return JSON.stringify(executionCandlesParamsCachePayload(params));
}

export type ExecutionCandlesLoadCallbacks = {
  onBackfillAccepted?: (partial: ExecutionCandlesResponse) => void;
  onBackfillProgress?: (progress: ExecutionCandlesBackfillProgress | null) => void;
};

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const id = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(id);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function throwIfOhlcStripFailed(poll: OhlcStripPollResponse): void {
  if (poll.status !== "failed") return;

  const authPayload = marketDataUnavailableFromBrokerAuthFailure(poll);
  if (authPayload) {
    throw new MarketDataUnavailableError(authPayload);
  }

  const message = poll.error_message?.trim();
  throw new Error(message || EXECUTION_CANDLES_SOURCE_UNAVAILABLE_MESSAGE);
}

async function pollOhlcStripUntilDoneInner(
  pollUrl: string,
  signal?: AbortSignal,
  intervalMs = 2500,
  maxWaitMs = 300_000,
  onProgress?: (progress: ExecutionCandlesBackfillProgress | null) => void
): Promise<OhlcStripPollResponse> {
  const deadline = Date.now() + maxWaitMs;
  let last: OhlcStripPollResponse | null = null;

  while (Date.now() < deadline) {
    const poll = await fetchOhlcStrip(pollUrl, signal);
    last = poll;
    onProgress?.(poll.progress ?? null);

    if (poll.status === "succeeded") return poll;
    if (poll.status === "failed") throwIfOhlcStripFailed(poll);
    if (poll.status === "expired") break;

    await sleep(intervalMs, signal);
  }

  throw new Error(EXECUTION_CANDLES_SOURCE_UNAVAILABLE_MESSAGE);
}

/** Poll strip until succeeded or expired; does not return chart data. */
export async function pollOhlcStripUntilDone(
  pollUrl: string,
  signal?: AbortSignal,
  intervalMs = 2500,
  maxWaitMs = 300_000,
  onProgress?: (progress: ExecutionCandlesBackfillProgress | null) => void
): Promise<OhlcStripPollResponse> {
  const inflight = ohlcStripPollsInFlight.get(pollUrl);
  if (inflight) return inflight;

  const promise = pollOhlcStripUntilDoneInner(
    pollUrl,
    signal,
    intervalMs,
    maxWaitMs,
    onProgress
  ).finally(() => {
    if (ohlcStripPollsInFlight.get(pollUrl) === promise) {
      ohlcStripPollsInFlight.delete(pollUrl);
    }
  });
  ohlcStripPollsInFlight.set(pollUrl, promise);
  return promise;
}

async function loadExecutionCandlesOnce(
  params: ExecutionCandlesFetchParams,
  callbacks?: ExecutionCandlesLoadCallbacks
): Promise<ExecutionCandlesResponse> {
  const requestParams: ExecutionCandlesFetchParams = {
    ...params,
    fill_missing: params.fill_missing ?? true,
  };

  const maxBackfillAttempts = 5;
  for (let attempt = 0; attempt < maxBackfillAttempts; attempt++) {
    try {
      return await fetchExecutionCandles(requestParams);
    } catch (err) {
      if (!(err instanceof ExecutionCandlesBackfillAccepted)) throw err;

      const partial = err.body;
      callbacks?.onBackfillAccepted?.(partial);
      callbacks?.onBackfillProgress?.(partial.progress ?? null);

      const pollUrl = partial.poll_url;
      if (!pollUrl) {
        throw new Error("Backfill accepted but poll_url missing");
      }

      await pollOhlcStripUntilDone(
        pollUrl,
        params.signal,
        2500,
        300_000,
        callbacks?.onBackfillProgress
      );
    }
  }

  throw new Error(EXECUTION_CANDLES_SOURCE_UNAVAILABLE_MESSAGE);
}

/**
 * Load execution candles with async backfill orchestration (202 → poll → retry).
 * Production UI should always pass fill_missing: true.
 */
export async function loadExecutionCandles(
  params: ExecutionCandlesFetchParams,
  callbacks?: ExecutionCandlesLoadCallbacks
): Promise<ExecutionCandlesResponse> {
  const requestParams: ExecutionCandlesFetchParams = {
    ...params,
    fill_missing: params.fill_missing ?? true,
  };
  const key = executionCandlesLoadKey(requestParams);
  const inflight = executionCandlesLoadsInFlight.get(key);
  if (inflight) return inflight;

  const promise = loadExecutionCandlesOnce(requestParams, callbacks).finally(() => {
    if (executionCandlesLoadsInFlight.get(key) === promise) {
      executionCandlesLoadsInFlight.delete(key);
    }
  });
  executionCandlesLoadsInFlight.set(key, promise);
  return promise;
}
